/**
 * Función auxiliar para convertir potenciales objetos Timestamp de Firestore a Date
 * @param {any} date - El objeto o string a convertir
 * @returns {Date} La instancia de Date resultante
 */
function toJSDate(date: any): Date {
  if (!date) return new Date(NaN);

  // Si ya es un objeto Date
  if (date instanceof Date) return date;

  // Si es un Timestamp de Firestore (tiene método toDate)
  if (typeof date.toDate === "function") return date.toDate();

  // Si es un objeto Timestamp plano (viene de una API o serialización)
  if (typeof date.seconds === "number") {
    return new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000);
  }

  if (typeof date._seconds === "number") {
    return new Date(date._seconds * 1000 + (date._nanoseconds || 0) / 1000000);
  }

  // Si es un string o número
  return new Date(date);
}

/**
 * Función para formatear una fecha de manera legible
 * @param {any} date - La fecha a formatear
 * @returns {string} La fecha formateada
 */
export function formatDate(date: any): string {
  const dateObj = toJSDate(date);

  if (isNaN(dateObj.getTime())) {
    return "Fecha inválida";
  }

  const now = new Date();
  const diffInMs = now.getTime() - dateObj.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  // Si es hoy
  if (diffInDays === 0) {
    if (diffInHours === 0) {
      if (diffInMinutes === 0) {
        return "Hace un momento";
      }
      return `Hace ${diffInMinutes} ${diffInMinutes === 1 ? "minuto" : "minutos"}`;
    }
    return `Hace ${diffInHours} ${diffInHours === 1 ? "hora" : "horas"}`;
  }

  // Si es ayer
  if (diffInDays === 1) {
    return "Ayer";
  }

  // Si es esta semana
  if (diffInDays < 7) {
    return `Hace ${diffInDays} ${diffInDays === 1 ? "día" : "días"}`;
  }

  // Si es este año
  if (dateObj.getFullYear() === now.getFullYear()) {
    return dateObj.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
    });
  }

  // Si es otro año
  return dateObj.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Función para formatear una fecha con hora
 * @param {any} date - La fecha a formatear
 * @returns {string} La fecha formateada con hora
 */
export function formatDateTime(date: any): string {
  const dateObj = toJSDate(date);

  if (isNaN(dateObj.getTime())) {
    return "Fecha inválida";
  }

  return dateObj.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Función para obtener la fecha relativa (hace X tiempo)
 * @param {any} date - La fecha a formatear
 * @returns {string} La fecha relativa
 */
export function getRelativeDate(date: any): string {
  const dateObj = toJSDate(date);

  if (isNaN(dateObj.getTime())) {
    return "Fecha inválida";
  }

  const now = new Date();
  const diffInMs = now.getTime() - dateObj.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  if (diffInDays > 0) {
    return `Hace ${diffInDays} ${diffInDays === 1 ? "día" : "días"}`;
  }

  if (diffInHours > 0) {
    return `Hace ${diffInHours} ${diffInHours === 1 ? "hora" : "horas"}`;
  }

  if (diffInMinutes > 0) {
    return `Hace ${diffInMinutes} ${diffInMinutes === 1 ? "minuto" : "minutos"}`;
  }

  return "Hace un momento";
}
